/**
 * Converts Shivaji legacy font text to Unicode Devanagari
 * Fixed version using the comprehensive mapping from unicode_to_shivaji.js
 * @param {string} inputText - Input text in Shivaji font encoding
 * @param {Object} options - Conversion options (for future use)
 * @returns {string} - Unicode Devanagari text
 */
export default function Shivaji_to_Unicode(inputText, options = {}) {
  if (!inputText || typeof inputText !== 'string') {
    return '';
  }

  // Comprehensive mapping for Shivaji to Unicode (reverse of unicode_to_shivaji mapping)
  const shivajiToUnicodeMap = {
    // Independent Vowels
    'A': 'अ', 'Aa': 'आ', '[': 'इ', '[-': 'ई', ']': 'उ', '}': 'ऊ',
    '?': 'ऋ', 'e': 'ए', 'eo': 'ऐ', 'Aao': 'ओ', 'AaO': 'औ', 'Aa^': 'ऑ', 'A^': 'ॲ', 'e^': 'ऍ',

    // Consonants (full form)
    'k': 'क', '@': 'क्', 'K': 'ख', '#': 'ख्', 'ga': 'ग', 'g': 'ग्',
    'Ga': 'घ', 'G': 'घ्', '=': 'ङ', '=\\': 'ङ्', 'ca': 'च', 'c': 'च्',
    'C': 'छ', 'ja': 'ज', 'j': 'ज्', 'Ha': 'ञ', 'H': 'ञ्', 'T': 'ट', 'D': 'ड',
    't': 'त', '%': 'त्', 'qa': 'थ', 'q': 'थ्', 'd': 'द', 'd\\': 'द्',
    'Qa': 'ध', 'Q': 'ध्', 'na': 'न', 'n': 'न्', 'p': 'प', 'P': 'प्',
    'f': 'फ', 'F': 'फ्', 'ba': 'ब', 'b': 'ब्', 'Ba': 'भ', 'B': 'भ्', 'ma': 'म', 'm': 'म्',
    'ya': 'य', 'y': 'य्', 'r': 'र', '-': 'र्', 'la': 'ल', 'l': 'ल्',
    'va': 'व', 'v': 'व्', 'Xa': 'श', 'X': 'श्', 'Ya': 'ष', 'Y': 'ष्', 'sa': 'स',
    's': 'स्', 'h': 'ह', 'Na': 'ण', 'N': 'ण्', 'L': 'ळ', 'Ja': 'झ', 'J': 'झ्',
    'z': 'ठ', 'Z': 'ढ', '&a': 'ज्ञ', '&': 'ज्ञ्', 'à': 'रु', '$': 'रू',

    // Special sequences and conjuncts (longest first)
    'savaa-MnaI': 'सर्वांनी', 'savaa-Mcaa': 'सर्वांचा', 'sauvaNaa-': 'सुवर्णा', 'savaa-odya': 'सर्वोदय',
    'saivh-sa': 'सर्व्हिस', 'ku;haDo': 'कुऱ्हाडे', 'saudXa-na': 'सुदर्शन', 'ANa-va': 'अर्णव',
    'icanmaya': 'चिन्मय', 'dXa-na': 'दर्शन', 'isawaqa-': 'सिद्धार्थ', 'Ajau-na': 'अर्जुन',
    'Aayaa-': 'आर्या', ']vaI-': 'उर्वी', 'eoXvayaa-': 'ऐश्वर्या', 'pUvaa-': 'पूर्वा',
    'rXmaI': 'रश्मी', 'caMd`': 'चंद्र', 'Xaadu-la': 'शार्दुल', 'AaXaIvaa-d': 'आशीर्वाद',
    'pUtI-': 'पूर्ती', 'icamau': 'चिमु', 'sava-': 'सर्व', 'gavaa-': 'गर्वा', 'Aqava-': 'अथर्व',
    'paqa-': 'पार्थ', 'Qa`uva': 'ध्रुव', 'ip`yaa': 'प्रिया', 'saUya-': 'सूर्य', 'kma-': 'कर्म',
    'sad\\gu$': 'सद्‌गुरू', 'dugaa-': 'दुर्गा', 'duga-o': 'दुर्गे', 'vaNa-': 'वर्ण',
    'vaNa-na': 'वर्णन', 'pUNa-': 'पूर्ण', 'XaIvaa-d': 'शीर्वाद', 'hYa-': 'हर्ष',
    '}jaa-': 'ऊर्जा', 'saamaqya-': 'सामर्थ्य', 'gaNaoXa': 'गणेश', 'nama:': 'नमः',
    'p`at:': 'प्रातः', 'puna:': 'पुनः', 'tp:': 'तपः', 'svat:': 'स्वतः', 'du:K': 'दुःख',

    // Complex conjuncts
    'xa': 'क्ष', 'x': 'क्ष्', '%na': 'त्न', '%ya': 'त्य', 'd\\gu': 'द्‌गु',
    'p`': 'प्र', 'ma-': 'र्म', 'ya-': 'र्य', 'Sla': 'श्ल', '~a': 'त्र', '~': 'त्र्',
    ')': 'हृ', 'ra^': 'रॉ', 'ga`': 'ग्र', 'laa^': 'लॉ', 'AMiba': 'अंबि', 'ba`': 'ब्र',
    'mha': 'ह्मा', 'yaa-': 'र्या', 'w': 'द्ध', 'k`': 'क्र', 'Ea': 'श्र', 'W': 'द्व',
    'isma': 'स्मि', 'd`u': 'द्रु', 'ôcaM': 'श्चं', 'iPna': 'प्नि', 'vaa-': 'र्वा',
    'iW': 'द्वि', 'TêU': 'ट्यू', 'd`': 'द्र', 'gaI-': 'र्गी', 'Ya-': 'र्ष', 't-': 'र्त',
    'ca-': 'र्च', 'p-': 'र्प', 'V': 'द्य', 'i#a`': 'ख्रि', 'Ga`': 'घ्र', 'ja`': 'ज्र',
    'YT/': 'ष्ट्र', 'D/': 'ड्र', 'Qaa-': 'र्धा', 'dI-': 'र्दी', 'tI-': 'र्ती',
    'qa-': 'र्थ', 'Xa-': 'र्श', 'T/': 'ट्र', 'Qa`': 'ध्र', 'na`': 'न्र', 'f`': 'फ्र',
    'f`a^': 'फ्रॉ', 'mh': 'ह्म', 'caa-': 'र्चा', 'ma`': 'म्र', 'maI-': 'र्मी',
    'Ba-': 'र्भ', 'ja-': 'र्ज', 'Qya-': 'र्ध्य', 'la-': 'र्ल', 'D-\\': 'र्ड्',
    'D-': 'र्ड', '¤': '+', '+': 'ट्ट', 'Û': '=', 'Ô': 'ड्ड', '_': 'द्द',
    '#a`': 'ख्र', 'Õ': 'ढ्ढ', 'maa-': 'र्मा', 'Na-': 'र्ण', 'bd': 'ब्द', 'ï': 'द्म',
    'Bya': 'भ्य', 'T-': 'र्ट', 'd-': 'र्द', 'na-': 'र्न', 'f-': 'र्फ', 'ba-': 'र्ब',
    'h-': 'र्ह', 'iw': 'द्धि', 'ik`': 'क्रि', 'sa-': 'र्स', 'ga-': 'र्ग', 'fI-': 'र्फी',
    'Ga-': 'र्घ', 'Yaa-': 'र्षा', 'ka^': 'कॉ', 'caa^': 'चॉ', 'sT/a^': 'स्ट्रॉ',
    'saç': 'सँ', 'insa': 'न्सि', 'isk': 'स्कि', 'isqa': 'स्थि', 'Qa-': 'र्ध',
    'iqa-': 'र्थि', 'da-': 'र्दो', 'va-': 'र्व', 'ta-': 'र्ता', 'Da^': 'डॉ',
    'qaI-': 'र्थी', 'ina-': 'र्नि', 'YTêaM': 'ष्ट्यां', 'i%va': 'त्वि', 'i%ya': 'त्यि',
    'iva-': 'र्वि', 'k-': 'र्क', 'vaI-': 'र्वी', 'vho-': 'र्व्ह', 'nao-': 'र्ने',
    'Ta^': 'टॉ', 'za^': 'ठॉ', 'gaa^': 'गॉ', 'ibd': 'ब्दि', 'qaa-M': 'र्थां',
    'iga-': 'र्गि', 'pid-': 'पर्दि', 'iHja': 'ञ्जि', 'iYa-': 'र्षि', 'iPt': 'प्ति',
    'DM': 'ड़', 'ha^': 'हॉ', 'sa`': 'स्र', 'is~a': 'स्त्रि',

    // Vowel signs with chandrabindu and visarga
    'Aaç': 'आँ', '[ç': 'इँ', 'kç': 'कँ', 'gaç': 'गँ', 'caç': 'चँ', 'jaç': 'जँ',
    'pç': 'पँ', 'baç': 'बँ', 'maç': 'मँ', 'igna': 'ग्नि', 'A:': 'अः', '[:': 'इः',
    ']:': 'उः', 'k:': 'कः', 'Aç': 'अँ', 'isT/': 'स्ट्रि', '!': 'ॐ',

    // More complex sequences
    'ip`': 'प्रि', 'isp': 'स्पि', 'iXca': 'श्चि', 'vaa-M': 'र्वां', 'Naa-': 'र्णा',
    'vaa-o': 'र्वो', 'sad\\gauà': 'सद्‌गुरु', 'Xva-': 'र्श्व', 'TI-': 'र्टी',
    'illa': 'ल्लि', 'ß': '|', 'igla': 'ग्लि', 'ivh': 'र्व्हि',
    'i)': 'ह्रि', 'iba`': 'ब्रि', 'iXa-': 'र्शि', 'iEa': 'श्रि', 'ilDM': 'ल्डिं',
    'va`': 'व्र', 'Lêa': 'ळ्या', 'in~a': 'न्त्रि', 'it-': 'र्ति', 'iNa-': 'र्णि',
    'gaa-': 'र्गो', 'NaI-': 'र्णी', 'isva': 'स्वि', 'Sva': 'श्‍व',

    // Consonant + Matra Combinations
    'ik': 'कि', 'iK': 'खि', 'iga': 'गि', 'iGa': 'घि', 'ica': 'चि',
    'iC': 'छि', 'ija': 'जि', 'iJa': 'झि', 'iT': 'टि', 'iz': 'ठि',
    'iD': 'डि', 'iZ': 'ढि', 'iNa': 'णि', 'it': 'ति', 'iqa': 'थि',
    'id': 'दि', 'iQa': 'धि', 'ina': 'नि', 'ip': 'पि', 'if': 'फि',
    'iba': 'बि', 'iBa': 'भि', 'ima': 'मि', 'iya': 'यि', 'ir': 'रि',
    'ila': 'लि', 'iva': 'वि', 'iXa': 'शि', 'iYa': 'षि', 'isa': 'सि',
    'ih': 'हि', 'ind': 'न्दि', 'i@t': 'क्ति', 'maid-': 'मर्दि',
    'maip-': 'मर्पि', 'imba': 'म्बि', 'i~a': 'त्रि', 'ilba': 'ल्बि',
    '{êa': 'ठ्ठ्या', ';yaa': 'ऱ्या', 'iND': 'ण्डि', 'i%ma': 'त्मि',
    'ixa': 'क्षि', 'sa`ao': 'स्रो', 'Xau': 'शु',
    'ku': 'कु', 'mau': 'मु', 'Tu': 'टु', '<aa': 'त्ता', '{': 'ठ्ठ',
    'iÇ': 'श्वि', 'Mba': 'ंब', 'XaI': 'शी',

    // Special Sequences and common patterns
    '`': '्र', 'EaI': 'श्री', 'saaO': 'सौ',

    // Common word patterns that need special handling
    'namaskaar': 'नमस्कार', 'kaso': 'कसे', 'Aahaat': 'आहात',
    'sagaLe': 'सगळे', 'sagaL': 'सगळ',

    // Vowel Signs (Matras)
    'a': 'ा', 'I': 'ी', 'i': 'ि', 'o': 'े', 'O': 'ै', 'R': 'ृ',
    'u': 'ु', 'U': 'ू', 'ao': 'ो', 'aO': 'ौ', '^': 'ॅ', 'ç': 'ँ',
    'a^': 'ॉ', 'M': 'ं', ':': 'ः',

    // Other Signs
    '\\': '्', '\\\\': '‌',

    // Punctuation
    '.': '।', '¯': '–', ',': ',', '¸': ';', '³': '(', '´': ')',
    'À': '/', 'è': '!',

    // Digits
    '0': '०', '1': '१', '2': '२', '3': '३', '4': '४',
    '5': '५', '6': '६', '7': '७', '8': '८', '9': '९',

    // Space
    ' ': ' '
  };

  // Main symbol replacement function
  const replaceSymbols = (modifiedSubstring) => {
    if (!modifiedSubstring) return '';

    let result = modifiedSubstring;

    // Apply character mappings (longest patterns first to avoid conflicts)
    const sortedKeys = Object.keys(shivajiToUnicodeMap).sort((a, b) => b.length - a.length);

    for (const shivajiChar of sortedKeys) {
      if (shivajiChar && shivajiToUnicodeMap[shivajiChar]) {
        // Use global replace to avoid infinite loops
        const regex = new RegExp(shivajiChar.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        result = result.replace(regex, shivajiToUnicodeMap[shivajiChar]);
      }
    }

    // Post-processing for special characters and positioning
    // Note: Most positioning should be handled by the comprehensive mapping above
    // Only handle cases that weren't covered by the mapping

    // Handle any remaining 'i' matra positioning issues
    // This should be minimal since the mapping should handle most cases
    let positionOfI = result.indexOf('i');
    while (positionOfI !== -1) {
      const nextChar = result.charAt(positionOfI + 1);
      // Only process if it's a standalone 'i' followed by a consonant
      if (nextChar && nextChar !== ' ' && nextChar !== 'i' &&
          /[क-ह]/.test(nextChar)) {
        const charToReplace = 'i' + nextChar;
        result = result.replace(charToReplace, nextChar + 'ि');
      }
      positionOfI = result.indexOf('i', positionOfI + 1);
    }

    // Fix wrong 'ि्' combinations (remove 'ि' from half consonants)
    let positionOfWrongEe = result.indexOf('ि्');
    while (positionOfWrongEe !== -1) {
      const consonantAfter = result.charAt(positionOfWrongEe + 2);
      if (consonantAfter && consonantAfter !== ' ' && /[क-ह]/.test(consonantAfter)) {
        const charToReplace = 'ि्' + consonantAfter;
        result = result.replace(charToReplace, '्' + consonantAfter + 'ि');
      }
      positionOfWrongEe = result.indexOf('ि्', positionOfWrongEe + 1);
    }

    return result;
  };

  // Process text in chunks for large texts (similar to original logic)
  const processTextInChunks = (text) => {
    const maxTextSize = 10000;
    const textSize = text.length;
    let processedText = '';
    let position1 = 0;
    let position2 = 0;
    let continueLoop = true;

    while (continueLoop) {
      position1 = position2;
      
      if (position2 < (textSize - maxTextSize)) {
        position2 += maxTextSize;
        // Find word boundary
        while (position2 > position1 && text.charAt(position2) !== ' ') {
          position2--;
        }
        if (position2 === position1) {
          // No space found, force break
          position2 = position1 + maxTextSize;
        }
      } else {
        position2 = textSize;
        continueLoop = false;
      }

      const chunk = text.substring(position1, position2);
      const processedChunk = replaceSymbols(chunk);
      processedText += processedChunk;
    }

    return processedText;
  };

  // HTML processing function
  const processHtmlContent = (htmlText) => {
    let remainingText = htmlText;
    let processedText = '';

    // Process Shivaji font spans
    let shivajiIndex = remainingText.indexOf('Shivaji');
    while (shivajiIndex !== -1) {
      const openBracket = remainingText.indexOf('>', shivajiIndex);
      let closingSpan = remainingText.indexOf('/span', openBracket);
      let openingSpan = remainingText.indexOf('span', openBracket);

      // Handle nested spans
      while (openingSpan < closingSpan && openingSpan !== -1) {
        openingSpan = remainingText.indexOf('span', closingSpan + 4);
        closingSpan = remainingText.indexOf('/span', closingSpan + 4);
      }

      if (openBracket !== -1 && closingSpan !== -1) {
        let modifiedSubstring = remainingText.substring(openBracket, closingSpan);
        modifiedSubstring = modifiedSubstring.replace(/>/g, '>>');
        
        processedText += remainingText.substring(0, openBracket) + modifiedSubstring + '/span';
        remainingText = remainingText.substring(closingSpan + 5);
        shivajiIndex = remainingText.indexOf('Shivaji');
      } else {
        break;
      }
    }

    processedText += remainingText;

    // Process font conversion in marked sections
    remainingText = processedText;
    processedText = '';
    
    let doubleBracket = remainingText.indexOf('>>');
    while (doubleBracket !== -1) {
      const openingBracket = remainingText.indexOf('>>', doubleBracket);
      const closingBracket = remainingText.indexOf('<', openingBracket);

      if (openingBracket !== -1 && closingBracket !== -1) {
        let modifiedSubstring = remainingText.substring(openingBracket + 2, closingBracket);
        
        // Handle HTML entities
        modifiedSubstring = modifiedSubstring.replace(/&gt;/g, 'क्त');
        modifiedSubstring = modifiedSubstring.replace(/&quot;/g, '"');
        modifiedSubstring = modifiedSubstring.replace(/&lt;/g, 'त्त्');
        modifiedSubstring = modifiedSubstring.replace(/&amp;/g, 'ज्ञ');

        const convertedSubstring = replaceSymbols(modifiedSubstring);
        
        processedText += remainingText.substring(0, openingBracket + 1);
        processedText += convertedSubstring + '<';
        remainingText = remainingText.substring(closingBracket + 1);
        doubleBracket = remainingText.indexOf('>>');
      } else {
        break;
      }
    }

    processedText += remainingText;

    // Final cleanup
    processedText = processedText.replace(/Shivaji01/g, 'mangal');
    processedText = processedText.replace(/ज्ञन्ब्स्प;/g, '/&nbsp');
    processedText = processedText.replace(/ाॅ/g, 'ॉ');

    return processedText;
  };

  // Handle HTML content if detected
  if (inputText.includes('<') && inputText.includes('>')) {
    return processHtmlContent(inputText);
  }

  // For short text, process directly to avoid chunking issues
  if (inputText.length < 1000) {
    return replaceSymbols(inputText);
  }

  // Process as plain text in chunks only for very large text
  return processTextInChunks(inputText);
}
